import React from 'react';
import { Link } from 'react-router-dom';

const BetaProgramNotice = () => {
  return (
    <div className="min-h-screen bg-slate-50 py-12 px-6">
      <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-sm p-8">
        <div className="mb-8">
          <Link to="/" className="text-[#0EA5E9] hover:text-[#0284c7] text-sm font-medium">
            ← Back to PassportTalk
          </Link>
        </div>
        
        <h1 className="text-4xl font-bold text-travel-navy font-outfit mb-4">Beta Program Notice</h1>
        <p className="text-sm text-slate-600 mb-8">
          <strong>Effective Date:</strong> January 1, 2025<br />
          <strong>Last Updated:</strong> January 5, 2025
        </p>

        <div className="bg-[#0EA5E9]/10 border-2 border-[#0EA5E9]/20 rounded-xl p-6 mb-8">
          <p className="text-lg font-semibold text-travel-navy mb-2">
            Thank you for being a PassportTalk Beta Tester!
          </p>
          <p className="text-slate-600">
            Your participation helps us build a better travel communication platform. This notice explains what beta access means and what you can expect.
          </p>
        </div>

        <div className="prose prose-slate max-w-none">
          <h2>1. What is Beta?</h2>
          <p>
            PassportTalk is in a <strong>beta testing phase</strong>. This means:
          </p>
          <ul>
            <li>The app is functional but still under active development</li>
            <li>Features may change, be added, or removed without notice</li>
            <li>You may encounter bugs, errors, or unexpected behavior</li>
            <li>Performance and reliability may vary</li>
            <li>Your feedback directly influences our development</li>
          </ul>

          <h2>2. What to Expect</h2>
          <h3>2.1 Working Features</h3>
          <ul>
            <li>AI-powered translation (English ↔ Spanish)</li>
            <li>Dialect-aware translations (Mexican, Dominican, Argentine, etc.)</li>
            <li>Gender-aware translation options</li>
            <li>Voice input and audio playback</li>
            <li>Saved favorites and translation history</li>
            <li>Live agent connection (via WhatsApp)</li>
            <li>Scenario-based translation contexts</li>
          </ul>

          <h3>2.2 Known Limitations</h3>
          <ul>
            <li>Currently supports English-Spanish only</li>
            <li>Agent availability varies by time of day</li>
            <li>WhatsApp calling requires WhatsApp installed</li>
            <li>Some features may have occasional delays</li>
          </ul>

          <h2>3. Beta User Responsibilities</h2>
          <p>As a beta user, you agree to:</p>
          <ul>
            <li>Use the service understanding its beta status</li>
            <li>Report bugs and issues you encounter</li>
            <li>Provide feedback when requested</li>
            <li>Not rely solely on PassportTalk for critical communications</li>
            <li>Keep your account secure</li>
          </ul>

          <h2>4. Service Level During Beta</h2>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 my-4">
            <p className="font-semibold text-amber-900 mb-2">No Guarantees During Beta</p>
            <ul className="text-sm text-amber-800">
              <li>No guaranteed uptime or availability</li>
              <li>No formal support SLA</li>
              <li>Features may be modified or removed</li>
              <li>Pricing may change</li>
            </ul>
          </div>

          <h2>5. Pricing During Beta</h2>
          <ul>
            <li><strong>AI Translation:</strong> Free during beta</li>
            <li><strong>Live Agent Calls:</strong> $2.00/minute (beta pricing)</li>
            <li>Prices subject to change after beta</li>
            <li>Beta users will be notified of pricing changes</li>
          </ul>

          <h2>6. Data During Beta</h2>
          <p>We collect additional usage data during beta to improve the service:</p>
          <ul>
            <li>Feature usage patterns</li>
            <li>Translation scenarios used</li>
            <li>Error logs and crash reports</li>
            <li>Performance metrics</li>
          </ul>
          <p>See our <Link to="/privacy" className="text-[#0EA5E9] hover:underline">Privacy Policy</Link> for full details.</p>

          <h2>7. Feedback</h2>
          <p>We want to hear from you! Share feedback via:</p>
          <ul className="list-none">
            <li><strong>Email:</strong> feedback@passtalkapp.com</li>
            <li><strong>In-App:</strong> Use the feedback option in your Profile</li>
          </ul>

          <h2>8. End of Beta</h2>
          <p>When beta ends:</p>
          <ul>
            <li>You&apos;ll be notified in advance</li>
            <li>Your account and data will be preserved</li>
            <li>Pricing may be adjusted for general availability</li>
            <li>Beta-only features may change</li>
          </ul>

          <h2>9. Contact</h2>
          <p><strong>Questions about the beta program:</strong></p>
          <ul className="list-none">
            <li><strong>Email:</strong> support@passtalkapp.com</li>
            <li><strong>Website:</strong> https://passtalkapp.com</li>
          </ul>
        </div>

        <div className="mt-12 pt-8 border-t border-slate-200">
          <p className="text-sm text-slate-600 text-center">
            <strong>Last Updated:</strong> January 5, 2025 | <strong>Version:</strong> 1.0 (Beta)
          </p>
          <p className="text-xs text-slate-500 text-center mt-4">
            Thank you for helping us build PassportTalk. Your feedback makes a difference!
          </p>
        </div>
      </div>
    </div>
  );
};

export default BetaProgramNotice;
